/**
 * Program Run — follow one run of a checkpoint program from Run to its end: running, paused and
 * waiting for a grant (with the grant to give right there), paused again for the next one, finished
 * or failed. The run is the program's checkpoint process, found from the jobevent Run made; it keeps
 * one process across all its pauses, so this dialog follows it through every grant.
 * FEATURE-20261005-program-authority-and-access-layer.
 *
 * Imperative trigger: openProgramRunDialog({ title, jobEventUid }) from anywhere. The Subject emits to
 * the always-mounted ProgramRunDialogHost.
 */
import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PauseCircleIcon from "@mui/icons-material/PauseCircle";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { ROUTES } from "../../routes/paths";
import { getCheckpointEngineRpcClient, type GrantScope, type ProgramStatus } from "../../services/checkpoint-engine-rpc-client";
import { showProgramRunDialog, type ProgramRunDialogRequest } from "./program-run-signal";

export const ProgramRunDialogHost: React.FC = () => {
  const [req, setReq] = useState<ProgramRunDialogRequest | undefined>(undefined);

  useEffect(() => {
    const sub = showProgramRunDialog.subscribe((next) => setReq(next));
    return () => sub.unsubscribe();
  }, []);

  return req ? <ProgramRunDialog key={req.jobEventUid} req={req} onClose={() => showProgramRunDialog.next(undefined)} /> : null;
};

type Kind = "running" | "paused" | "granted" | "finished" | "failed";

interface Step {
  kind: Kind;
  label: string;
}

const POLL_MS = 1500;

const SCOPES: { scope: GrantScope; label: string; done: string }[] = [
  { scope: "run", label: "Grant for this run", done: "for this run" },
  { scope: "job", label: "Grant for the job", done: "for the job" },
  { scope: "worker", label: "Grant for the worker (every job it runs)", done: "for the worker" },
];

const terminal = (s?: ProgramStatus) => s?.state === "finished" || s?.state === "failed" || s?.state === "lost";

// A returned value as a person reads it: a string as its text, anything else as JSON.
function shown(valueJson: string): string {
  try {
    const v: unknown = JSON.parse(valueJson);
    return typeof v === "string" ? v : JSON.stringify(v);
  } catch {
    return valueJson;
  }
}

// The step a status adds to the history, keyed so the same status is added once.
function stepOf(s: ProgramStatus): { key: string; step: Step } | undefined {
  if (!s.found) return undefined;
  switch (s.state) {
    case "running":
    case "spawned":
      return { key: "running", step: { kind: "running", label: "Running" } };
    case "paused":
      return s.needsGrant
        ? { key: `paused:${s.checkpointUid}`, step: { kind: "paused", label: `Paused: it needs a grant of ${s.needsGrant}` } }
        : { key: `paused:${s.checkpointUid}`, step: { kind: "paused", label: "Paused: waiting to be resumed" } };
    case "finished":
      return { key: "finished", step: { kind: "finished", label: s.valueJson && s.valueJson !== "null" ? `Finished: ${shown(s.valueJson)}` : "Finished" } };
    case "failed":
      return { key: "failed", step: { kind: "failed", label: `Failed: ${s.message || "the program ended with an error"}` } };
    case "lost":
      return { key: "lost", step: { kind: "failed", label: "Lost: the worker running it stopped reporting" } };
  }
  return undefined;
}

function stepIcon(kind: Kind, last: boolean) {
  switch (kind) {
    case "running":
      return last ? <CircularProgress size={20} /> : <PlayCircleIcon color="action" />;
    case "paused":
      return <PauseCircleIcon color={last ? "warning" : "action"} />;
    case "granted":
      return <LockOpenIcon color="success" />;
    case "finished":
      return <CheckCircleIcon color="success" />;
    case "failed":
      return <ErrorIcon color="error" />;
  }
}

const ProgramRunDialog: React.FC<{ req: ProgramRunDialogRequest; onClose: () => void }> = ({ req, onClose }) => {
  const [status, setStatus] = useState<ProgramStatus | undefined>(undefined);
  const [steps, setSteps] = useState<Step[]>([]);
  const [error, setError] = useState<string | undefined>(undefined);
  const [granting, setGranting] = useState(false);
  const lastKey = useRef("");
  // the pause just granted: until the program leaves it, a poll still reading it is not a new pause
  const granted = useRef("");
  const processUid = useRef("");
  const open = useRef(true);
  useEffect(() => () => void (open.current = false), []);

  // Poll the run's process until it ends: by the jobevent until the run has started, then by the
  // process, which every later turn (each resume after a grant) belongs to.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const s = await getCheckpointEngineRpcClient().programStatus(
          processUid.current ? { processUid: processUid.current } : { jobEventUid: req.jobEventUid },
        );
        if (!open.current) return;
        if (s.processUid) processUid.current = s.processUid;
        const stale = s.state === "paused" && Boolean(s.checkpointUid) && s.checkpointUid === granted.current;
        if (stale) {
          if (open.current) timer = setTimeout(() => void tick(), POLL_MS);
          return;
        }
        setStatus(s);
        const next = stepOf(s);
        if (next && next.key !== lastKey.current) {
          lastKey.current = next.key;
          setSteps((prev) => [...prev, next.step]);
        }
        if (terminal(s)) return;
      } catch (e) {
        if (open.current) setError(e instanceof Error ? e.message : String(e));
      }
      if (open.current) timer = setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
    return () => timer && clearTimeout(timer);
  }, [req.jobEventUid]);

  const grant = async (scope: GrantScope, done: string) => {
    if (!status?.checkpointUid || !status.needsGrant) return;
    setGranting(true);
    setError(undefined);
    try {
      const r = await getCheckpointEngineRpcClient().grantProgram(status.checkpointUid, scope);
      setSteps((prev) => [...prev, { kind: "granted", label: `Granted ${r.capability} ${done}; it goes on` }]);
      // the next status is the program running again, or paused for its next need
      granted.current = status.checkpointUid;
      lastKey.current = "";
      setStatus({ ...status, state: "running", needsGrant: "" });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGranting(false);
    }
  };

  const waitingForGrant = status?.state === "paused" && Boolean(status.needsGrant);

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{req.title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <List dense data-testid="program-run-steps">
          {steps.length === 0 && (
            <ListItem disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}><CircularProgress size={20} /></ListItemIcon>
              <ListItemText primary="Starting on the worker" />
            </ListItem>
          )}
          {steps.map((step, i) => (
            <ListItem key={i} disableGutters>
              <ListItemIcon sx={{ minWidth: 36 }}>{stepIcon(step.kind, i === steps.length - 1 && !terminal(status))}</ListItemIcon>
              <ListItemText primary={step.label} />
            </ListItem>
          ))}
        </List>
        {waitingForGrant && (
          <Alert severity="warning" sx={{ mt: 1 }} data-testid="program-run-needs-grant">
            <Typography variant="body2" sx={{ mb: 1 }}>
              The program stopped before this, and goes on from here once it is granted:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace", wordBreak: "break-all", mb: 1.5 }}>
              {status?.needsGrant}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
              {SCOPES.map((s) => (
                <Button key={s.scope} size="small" variant="contained" disabled={granting} onClick={() => void grant(s.scope, s.done)}>
                  {s.label}
                </Button>
              ))}
            </Stack>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<OpenInNewIcon />}
          disabled={!status?.processUid}
          onClick={() => status?.processUid && window.open(ROUTES.APP.LOGS_WITH_PARAMS(status.processUid, "console"), "_blank")}
        >
          Open console
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
